import React, { useState, useEffect, useRef } from "react";
import axios from "axios";

import { ReactComponent as AlertIcon } from '../../../public/assets/css/ontario-design-system/icons/svg/ontario-icon-alert-information.svg';
import { APIKey, FormSectionElements, ProgramContainer } from "../../entities/formTemplateDataType";
import { getViewportSize } from "../../utilities/HtmlUtils";
import { getCleansedUrl, getAppNameFromPath, normalizeUrl } from "../../utilities/URLUtils";
import { SuccessAlertComponent } from "../odsCalloutBox/SuccessAlertComponent";
import { EmailButtonComponent } from "./EmailButtonComponent";
import { Heading5 } from "../../theme";
import Utils from "../../utilities/Utils";
import DataUtils, {isNullOrEmpty, sanitizeEmail, sanitizeString} from "../../utilities/DataUtils";
import TrackPageView from "../../utilities/TrackPageView";
import { getEnvVar } from "../../config/RuntimeConfig";
import OrderInfoBreakdownComponent from "./order/OrderInfoBreakdownComponent";
import { setBaseHeaders } from "../../utilities/submitUtils";

interface Props {
  field: FormSectionElements;
  programContainer: ProgramContainer;
  isEng: boolean;
  callbackElement,
}

const OrderConfirmationComponent = ({field, programContainer, isEng}: Props) => {
  const cleansedUrl = new URL(getCleansedUrl(window.location.href));
  const transactionIdFromUrl = cleansedUrl.searchParams.get('transactionId');
  const orderTransactionIdFromUrl = cleansedUrl.searchParams.get('orderTransactionId');
  const profile = programContainer?.Profile;  // Check if user is logged in (has userId)

  // State for email sent feedback
  const [emailSent, setEmailSent] = useState(false);
  const [emailSentTo, setEmailSentTo] = useState('');
  const [emailError, setEmailError] = useState(false);
  const hasTrackedRef = useRef(false);

  const resolveConfirmationSlug = (useFrenchTitle: boolean) => {
    const title =
      (useFrenchTitle ? field.confirmationTitleFRA : field.confirmationTitle) ||
      field.confirmationTitle ||
      field.pageName;
    if (!title) {
      return "";
    }
    return title
      .trim()
      .replace(/[^a-zA-Z0-9]/g, "-")
      .replace(/^-+|-+$/g, "")
      .replace(/-+$/g, "")
      .toLowerCase();
  };

  //------------------------------------------
  //useEffect
  //------------------------------------------
    const updateConfirmationUrlAndTracking = () => {
    const isFrenchPath =
      window.location.pathname.includes("/fr/") ||
      window.location.hash.includes("/fr/") ||
      window.location.href.includes("/on-form/fr/");
    const slug = resolveConfirmationSlug(isFrenchPath);
    const appName = (programContainer?.CategoryId || getAppNameFromPath() || "").toLowerCase();

    if (!slug || !appName) {
      return;
    }
    if (slug && appName) {       
        sessionStorage.removeItem(`OrderConfirmationSummaryFired-${appName}-en`);
        sessionStorage.removeItem(`OrderConfirmationSummaryFired-${appName}-fr`);
        sessionStorage.removeItem(`OrderConfirmationSummaryFired-${appName}`);
   }

   sessionStorage.removeItem("languageChange");

    const basePath = window.location.origin + "/on-form" + (isFrenchPath ? "/fr" : "") + "/" + appName;
    const urlString = normalizeUrl(`${basePath}/${slug}`);
    const langKey = isFrenchPath ? "fr" : "en";
    const trackingId = transactionIdFromUrl || slug;
    const trackingKey = `OrderConfirmationSummaryFired-${appName}-${langKey}-${trackingId}`;

    const existingPreviousUrl = sessionStorage.getItem("previousUrl");

    if (!field.disableConfirmationUrlRewrite) {
        window.history.pushState({}, "", urlString);
    }
   
    //---------------TrackPageView code in OrderConfiramtion  notes---------------------------------------//
    //this condition checking is for:
    //In project EHOCM, 
    //there is a summary page before the order confirmation page. 
    //It will cause the value of "this.state.stepperIndex" equal to the value of "prevState.stepperIndex" in FormPreviewComponent
    //TrackPageView will not be called in FormPreviewComponent
    //
    //In project OER, 
    //there is NOT a summary page before the order confirmation page. 
    //The value of "this.state.stepperIndex" does not equal to the value of "prevState.stepperIndex" in FormPreviewComponent
    //TrackPageView will BE CALLED in FormPreviewComponent. OrderConfiramtion will not call TrackPageView again.
    if (existingPreviousUrl !== urlString)
    {
    if (sessionStorage.getItem(trackingKey) === "true") {
      hasTrackedRef.current = true;
      sessionStorage.removeItem(trackingKey);
    }

    if (!hasTrackedRef.current && sessionStorage.getItem(trackingKey) !== "true") {
      TrackPageView(programContainer);
      sessionStorage.setItem(trackingKey, "true");
      }
    }
    //---------------end of code notes---------------------------------------//
  };

  // Track page view and update URL on mount/visibility changes
  useEffect(() => {
    if (!programContainer) {
      return;
    }
    if (field?.confirmationTitle || field?.confirmationTitleFRA || field?.pageName) {  
      updateConfirmationUrlAndTracking();
    } 
    else if (programContainer) {
      TrackPageView(programContainer);
    }
  }, [
    programContainer,
    isEng,
    field?.confirmationTitle,
    field?.confirmationTitleFRA,
    field?.pageName,
  ]);
  //------------------------------------------
  //end of Re-write -useEffect
  //------------------------------------------

  // Try to get the confirmation ID from API response, sessionStorage, or URL parameter
  const getConfirmationId = () => {
    if ((programContainer.CategoryId.toLowerCase() === 'glp') || (programContainer.CategoryId.toLowerCase() === 'plp')){ // A temporary fix.
      return orderTransactionIdFromUrl;
    }

    // 0. Check if field has apiResponse with dId
    if (programContainer.Response?.data?.contentAttributes) {  //OER confirmation number gets from email content value
    return programContainer.Response.data.contentAttributes.find(attr=>attr.dIdNumber)?.dIdNumber;     
    }
    // 1. Check if field has apiResponse with id
    else if (field.apiResponse?.id) {
      return field.apiResponse.id;
    }   

    // 2. Check sessionStorage (saved by axios interceptor)
    try {
      const storedResponse = sessionStorage.getItem('submitApiResponse');
      if (storedResponse) {
        const parsed = JSON.parse(storedResponse);
        if (parsed.id) {
          return parsed.id;
        }
      }
    } catch (e) {
      // Silent fail
    }

    // 3. Fall back to URL parameter
    return transactionIdFromUrl;
  };

   // Try to get the confirmation ID from API response, sessionStorage, or URL parameter
  const getConfirmationDate = () => {
     // Check if field has apiResponse with creationTimestamp
     if (programContainer.Response?.data?.contentAttributes) {  //OER create date gets from email content value
      return programContainer.Response.data.contentAttributes.find(attr=>attr.creationDate)?.creationDate;
    }  

    // Fall back to URL parameter
    return transactionIdFromUrl;
  };

  const transactionId = getConfirmationId();
  const transactionDate = getConfirmationDate();
  const receiptTransactionId = transactionIdFromUrl || transactionId;
  
  
  const resolvePlaceholders = (url: string, txId: string): string => {
    if (!url) return url;
    const placeholderPattern = /\{\{([^}]+)\}\}/g;
    const resolved = url.replace(placeholderPattern, (placeholder, fieldName) => {
      const name = fieldName.trim();
      if (name === 'transactionId') return encodeURIComponent(txId ?? '');
      if (name === 'lang') return isEng ? 'en' : 'fr';
      const sections = programContainer?.ListOfFormSections ?? [];
      for (const section of sections) {
        for (const sub of section.ListOfFormSubSections ?? []) {
          const found = (sub.ListOfFormSectionElements ?? []).find(
            (ele: any) => ele.LoadBindName === name
          );
          if (found) {
            const raw = String(found.Value ?? found.SelectionCode ?? '');
            // If the value is already an absolute URL, use it as-is to avoid double-encoding
            if (/^https?:\/\//i.test(raw)) return raw;
            return encodeURIComponent(raw);
          }
        }
      }
      return placeholder;
    });
    if (resolved && !/^https?:\/\//i.test(resolved)) {
      return (resolved.startsWith('//') ? 'https:' : 'https://') + resolved.replace(/^\/\//, '');
    }
    return resolved;
  };

  const downloadReceipt = (receiptTransactionId: string) => {
    const rawDownloadPDFUrl = (isEng ? !Utils.isNullOrEmpty(field.downloadPDFURL) : !Utils.isNullOrEmpty(field.downloadPDFURLFRA))? (isEng ? field.downloadPDFURL : field.downloadPDFURLFRA) : (!isEng && Utils.isNullOrEmpty(field.downloadPDFURLFRA)  && !Utils.isNullOrEmpty(field.downloadPDFURL)) ? field.downloadPDFURL : "";
    const downloadPDFUrl = resolvePlaceholders(rawDownloadPDFUrl, receiptTransactionId);
    const downloadPDAPIKey = !Utils.isNullOrEmpty(field.downloadPDFApiKeyKey) ? field.downloadPDFApiKeyKey : ""; 
    const downloadPDAPIKeyValue = !Utils.isNullOrEmpty(field.downloadPDFApiKeyValue) ? field.downloadPDFApiKeyValue : ""; 
   
    //get all of download PDf file URL and Api key from Order confirmation component fill in fields
    if(downloadPDFUrl != "" && downloadPDAPIKey != "" && downloadPDAPIKeyValue != ""){  
         const data = programContainer?.Response?.data;

          fetch(downloadPDFUrl, {
              method: "POST",
              headers: {
                  "Content-Type": "application/json",
                  [downloadPDAPIKey]: downloadPDAPIKeyValue
              } ,
              body: JSON.stringify(data, (key, value) =>
               key&&key.includes("url_of_") ? sanitizeString(value) : value)
             })
              .then(resp => resp.arrayBuffer())
              .then(buf => {
                const blob = new Blob([buf], { type: "application/pdf" });
                const anchor = document.createElement("a");

            anchor.href = window.URL.createObjectURL(blob);
            anchor.download =programContainer?.Response?.data?.attachmentPdfName ? programContainer.Response.data.attachmentPdfName: `Receipt-${receiptTransactionId}`;
            anchor.hidden=true;

            document.body.append(anchor);
            anchor.click();
            anchor.remove();

            // in case the Blob uses a lot of memory
            window.addEventListener(
              "focus",
              () => {
                URL.revokeObjectURL(anchor.href);
              },
              {
                once: true,
              }
            )
          }) .catch(err => console.error("Download error:", err)); 
    }
    else if (downloadPDFUrl != "") {
      fetch(downloadPDFUrl)
        .then(resp => resp.arrayBuffer())
        .then(buf => {
          const blob = new Blob([buf], { type: "application/pdf" });
          const anchor = document.createElement("a");
          anchor.href = window.URL.createObjectURL(blob);
          anchor.download = `Receipt-${receiptTransactionId}`;
          anchor.hidden = true;
          document.body.append(anchor);
          anchor.click();
          anchor.remove();
          window.addEventListener("focus", () => URL.revokeObjectURL(anchor.href), { once: true });
        })
        .catch(err => console.error("Download error:", err));
    }
    //get all of download file URL and Api key from env setting value
    else{
      // Construct the filename to '<category-id>-<language>-<transaction-id>' format.
    const fileNameSeparator = '-';
    const categoryId = programContainer.CategoryId.toLowerCase();
    const language = isEng ? 'en' : 'fr';
    const fileName = categoryId + fileNameSeparator + language + fileNameSeparator + receiptTransactionId;
    const url = getEnvVar('DOWNLOAD_RECEIPT_URL').replace("{transactionID}", fileName);
    fetch(url).then(resp => resp.arrayBuffer())
    .then(buf => {
      const blob = new Blob([buf], { type: "application/pdf" });
      const anchor = document.createElement("a");

      anchor.href = window.URL.createObjectURL(blob);
      anchor.download = `Receipt-${receiptTransactionId}`;
      anchor.hidden=true;

      document.body.append(anchor);
      anchor.click();
      anchor.remove();

      // in case the Blob uses a lot of memory
      window.addEventListener(
        "focus",
        () => {
          URL.revokeObjectURL(anchor.href);
        },
        {
          once: true,
        }
      )
    });
    }

  }
 
  const HtmlToReactParser = require('html-to-react').Parser;
  const htmlToReactParser = new HtmlToReactParser();

  // For guest users, remove the "You may track this complaint" paragraph
  // For logged-in users, ensure "My transaction" is a clickable link
  const getFilteredRichText = () => {
    let richText = isEng ? field.ErrorMessageRichText : field.ErrorMessageRichTextFRA;
    if (!richText) return richText;

    if (!profile?.userId) {
      // For guest users, remove the "You may track this complaint" paragraph entirely
      richText = richText.replace(/<p>You may track this complaint.*?<\/p>/gi, '');
      richText = richText.replace(/<p>Vous pouvez suivre cette plainte.*?<\/p>/gi, '');
    } else {
      // For logged-in users, ensure "My transaction" / "Mon dossier" is a link
      const myTransactionLinkEng = `<a href="${getEnvVar('PUBLIC_CXP_WEB_BASE_PATH')}/transactions" rel="noopener noreferrer" target="_blank">My transaction</a>`;
      const myTransactionLinkFra = `<a href="${getEnvVar('PUBLIC_CXP_WEB_BASE_PATH')}/fr/transactions" rel="noopener noreferrer" target="_blank">Mon dossier</a>`;

      if (isEng && !richText.includes('My transaction</a>')) {
        richText = richText.replace(/My transaction/g, myTransactionLinkEng);
      } else if (!isEng && !richText.includes('Mon dossier</a>')) {
        richText = richText.replace(/Mon dossier/g, myTransactionLinkFra);
      }
    }
    return richText;
  };
  const toDoNextBulletPointHtml = htmlToReactParser.parse(getFilteredRichText());
  const confirmationDescriptionHtml = htmlToReactParser.parse(isEng? field.confirmationDescription: field.confirmationDescriptionFRA);
  const confirmationDate =  htmlToReactParser.parse(isEng? field.orderDateLabel: field.orderDateLabelFRA);
  const viewPortSize = getViewportSize();

  const downloadPDFTextLength = (field.downloadPDFButtonLabel || "Download PDF confirmation")?.length?? 0;
  const downloadPDFFRATextLength = (field.downloadPDFButtonLabelFRA ||"Télécharger la confirmation PDF")?.length?? 0;
  const confirmationButtonClassName = downloadPDFTextLength > 20 ? "ontario-button ontario-button--primary confirmation-button" : "ontario-button ontario-button--primary";
  const confirmationButtonFRAClassName = downloadPDFFRATextLength > 20 ? "ontario-button ontario-button--primary confirmation-button" : "ontario-button ontario-button--primary";

  // Get email for confirmation message: only for logged-in users or 'contactEmailAddress' was in the order.
  const contactEmailAddress = field.findFieldByName('contactEmailAddress')?.Value;
  const emailConfirmationMsg =  (profile?.email || contactEmailAddress)
  ? (isEng
        ? `An email confirmation was sent to ${profile?.email || contactEmailAddress}.`
        : `Un courriel de confirmation a été envoyé à ${profile?.email || contactEmailAddress}.`)
    : '';

 //Get email for confirmation message from callOutBoxBody/callOutBoxBodyFRA
  const accountInboxLinkEng = `<a href="${getEnvVar('PUBLIC_CXP_WEB_BASE_PATH')}/notifications/inbox" rel="noopener noreferrer" target="_blank">account inbox</a>`;
  const accountInboxLinkFra = `<a href="${getEnvVar('PUBLIC_CXP_WEB_BASE_PATH')}/fr/notifications/inbox" rel="noopener noreferrer" target="_blank">votre compte</a>`;
  const emailConfirmationMsgFromCallOutBoxBodyEng =  !isNullOrEmpty(field.callOutBoxBody)  ? (field.callOutBoxBody.replace("{email}", profile?.email).replace("{accountinbox}", accountInboxLinkEng ))  : '';
  const emailConfirmationMsgFromCallOutBoxBodyFra =  !isNullOrEmpty(field.callOutBoxBodyFRA)  ? (field.callOutBoxBodyFRA.replace("{email}", profile?.email).replace("{accountinbox}", accountInboxLinkFra ))  : '';
  const emailConfirmationMsgFromCallOutBoxBody =  
   isEng? 
   emailConfirmationMsgFromCallOutBoxBodyEng : emailConfirmationMsgFromCallOutBoxBodyFra;
  
  return (
    <div>
      <div>
        <SuccessAlertComponent field={field}
          title={isEng ? field.callOutBoxTitle : field.callOutBoxTitleFRA}
          bodyMsg= { emailConfirmationMsgFromCallOutBoxBody !== "" ? emailConfirmationMsgFromCallOutBoxBody  : emailConfirmationMsg}
          isEng={isEng} Visible={true} />
      </div>
      {(isEng ? field.confirmationTitle : field.confirmationTitleFRA) && (
        <h2 className="ontario-h1">{isEng ? field.confirmationTitle : field.confirmationTitleFRA}</h2>
      )}
      <div>
        <span className="ontario-h5">{isEng ? (field.orderIdLabel || "Order ID") + ": " : (field.orderIdLabelFRA || "Numéro de commande") + " : "}</span>
        {profile?.userId && !field.noMyTransactionsLink ? (
          <>
            {isEng
              ? "Your issue number will be ready within 30 minutes. Once it is available, you can view it under "
              : "Votre numéro de dossier sera disponible dans 30 minutes. Une fois disponible, vous pourrez le consulter dans "}
            <a
              href={isEng ? `${getEnvVar('PUBLIC_CXP_WEB_BASE_PATH')}/transactions` : `${getEnvVar('PUBLIC_CXP_WEB_BASE_PATH')}/fr/transactions`}
              rel="noopener noreferrer"
              target="_blank"
            >
              {isEng ? "My transactions" : "Mes transactions"}
            </a>
            {isEng ? "." : "."}
          </>
        ) : (
          transactionId
        )}
      </div>
      {profile?.userId && ( 
        confirmationDate != null ?  
         <div>       
          <span className="ontario-h5">{isEng ? (field.orderDateLabel || "Order Date") + ": " : (field.orderDateLabelFRA || "Date de commande") + " : "}</span> {transactionDate}
         </div> : 
         <div>
           <span className="ontario-h5">{isEng ? "Date: " : "Date : "}</span>
           {new Date().toISOString().split('T')[0]}
         </div>
      )}
      {field.showOrderInfoSection && <OrderInfoBreakdownComponent
        field={field} isEng={isEng}/>}
     
      <div>
         <div className="ontario-h5"></div>
         {confirmationDescriptionHtml}
      </div>
      <p></p>
      <div>
        <button className={isEng ?confirmationButtonClassName : confirmationButtonFRAClassName} disabled={false} onClick={() => downloadReceipt(receiptTransactionId)}>
            {isEng ? (field.downloadPDFButtonLabel || "Download PDF confirmation") : (field.downloadPDFButtonLabelFRA ||"Télécharger la confirmation PDF")}
        </button>
      </div>
      <div style={{display: 'flex', alignItems: 'flex-start'}} className="ontario-margin-bottom-32-!">
        <AlertIcon style={{ viewBox: "0 0 24 24",
           width: viewPortSize.width < 400 ? "30%" : viewPortSize.width < 600 ? "20%" : viewPortSize.width < 800 ? "10%" : "3%",
           marginRight: viewPortSize.width < 400 ? '0.5rem' : '0', marginTop: viewPortSize.width < 400 ? '0.15rem' : '0.1rem' }}/>
        <span>{isEng ? field.EmptyErrorMessage : field.EmptyErrorMessageFRA}</span>
      </div>

      {(field.displayEmailFieldForAllUsersAtBottom &&    
        <div>      
          <div>
            {toDoNextBulletPointHtml}
          </div>
        </div>
      )} 
      {/* Email field - show only for guest users when enabled (optional or required) or show for both logged-in & Guest users when displayEmailFieldForAllUsers is enabled*/}
      {(field.showEmailField === 'optional' || field.showEmailField === 'required') && (field.displayEmailFieldForAllUsersAtBottom || !profile?.userId) && (
        <div className="ontario-margin-bottom-32-!">
          {emailSent ? (
            <div className="ontario-alert ontario-alert--informational">
              <div className="ontario-alert__header">
                <div className="ontario-alert__header-icon">
                  <svg className="ontario-icon" aria-hidden="true" focusable="false" viewBox="0 0 24 24" preserveAspectRatio="xMidYMid meet">
                    <use href="#ontario-icon-alert-information"></use>
                  </svg>
                </div>
                <h2 className="ontario-alert__header-title ontario-h4">
                  {isEng ? "Email sent" : "Courriel envoyé"}
                </h2>
              </div>
              <div className="ontario-alert__body">
                <p>
                  {isEng
                    ? `A confirmation email has been sent to ${emailSentTo}`
                    : `Un courriel de confirmation a été envoyé à ${emailSentTo}`}
                </p>
              </div>
            </div>
          ) : (
            <>
              {emailError && (
                <div className="ontario-alert ontario-alert--error ontario-margin-bottom-16-!">
                  <div className="ontario-alert__header">
                    <div className="ontario-alert__header-icon">
                      <svg className="ontario-icon" aria-hidden="true" focusable="false" viewBox="0 0 24 24" preserveAspectRatio="xMidYMid meet">
                        <use href="#ontario-icon-alert-error"></use>
                      </svg>
                    </div>
                    <h2 className="ontario-alert__header-title ontario-h4">
                      {isEng ? "Failed to send email" : "Échec de l'envoi du courriel"}
                    </h2>
                  </div>
                  <div className="ontario-alert__body">
                    <p>
                      {isEng
                        ? "There was a problem sending the confirmation email. Please try again."
                        : "Un problème est survenu lors de l'envoi du courriel de confirmation. Veuillez réessayer."}
                    </p>
                  </div>
                </div>
              )}
              <EmailButtonComponent
                key={"EmailComponent"}
                field={field}
                editable={true}
                disabled={false}
                enableRecaptcha={!!field.Recaptcha || field.SubmitButton?.enableRecaptcha}
                title={
                  <>
                    <label className="ontario-h4">
                      {isEng
                        ? (field.emailFieldTitle || "Send confirmation to this email address")
                        : (field.emailFieldTitleFRA || "Envoyer la confirmation à cette adresse courriel")}
                      {' '}
                     {/* Updated styles for “optional” / “required” text as per Ontario Design System (ODS) */}
                      <span style={{
                            fontFamily: "'Open Sans', 'Helvetica Neue', Helvetica, Arial, sans-serif",
                            fontSize: 16,
                            fontWeight: 400,
                          }}
                        >
                        {field.showEmailField === 'optional'
                          ? (isEng ? '(optional):' : '(facultatif) :')
                          : (isEng ? '(required):' : '(obligatoire) :')}
                      </span>
                    </label>
                    <p style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>
                      {isEng
                        ? (field.emailFieldDescription || "Your email address will only be used to send you a transaction confirmation and will not be retained.")
                        : (field.emailFieldDescriptionFRA || "Votre adresse courriel ne servira qu'à vous envoyer une confirmation de transaction et ne sera pas conservée.")}
                    </p>
                  </>  
                }   
                labelControlArray={null}
                requiredField={field.showEmailField === 'required'}
                isEng={isEng}
                callback={(emailField, token) => {
                  setEmailError(false); // Clear previous error
                  // Send all data in ProgramContainer as payment if configured.
                  const payload =  field.sendWholeContainerAsPayload ? (() => {
                    // Collect all data from ProgramContainer as payload
                    const entity = DataUtils.collectFormValue(programContainer);
                      return { 
                        ...entity, 
                        language: isEng ? "EN" : "FR",
                        token
                      };
                  })(): {
                      email: emailField.Value,
                      transactionId,
                      lang: isEng ? "EN" : "FR",
                  };                       
                  const url = field.emailSendApi || getEnvVar('SEND_EMAIL_URL') || getEnvVar('EMAIL_URL');
                  if (url) {
                    // Include API-key in header if provided.
                    const apiKeyKey = field.ApiKeyKey || field.SubmitButton?.apiKeyKey;
                    const apiKeyValue = field.ApiKeyValue || field.SubmitButton?.apiKeyValue;
                    const apiKey: APIKey =
                      apiKeyKey && apiKeyValue
                        ? { key: apiKeyKey, value: apiKeyValue }
                        : programContainer.ApiKeyKey && programContainer.ApiKeyValue
                        ? {
                            key: programContainer.ApiKeyKey,
                            value: programContainer.ApiKeyValue,
                          }
                        : null;
                    const headers = setBaseHeaders(
                      "application/json",
                      null,
                      apiKey
                    );
                    const requestConfig: any = { headers };
                    axios.post(url, payload, requestConfig)
                      .then((res) => {
                        console.log(res);
                        setEmailSent(true);
                        setEmailSentTo(emailField.Value);
                      })
                      .catch((ex) => {
                        console.log(ex);
                        setEmailError(true);
                      });
                  } else {
                    console.warn('Email Send API is not configured');
                    setEmailError(true);
                  }
                }}
              />
            </>
          )}
        </div>
      )}
      {(!field.displayEmailFieldForAllUsersAtBottom &&    
          <div>      
            <div>
              {toDoNextBulletPointHtml}
            </div>
          </div>
        )}   
 
    </div>
  );
}

export default OrderConfirmationComponent;



